// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Enhanced error handling for AI Triage functionality
 * Provides detailed error categorization, user guidance, and retry mechanisms
 */

/**
 * Detailed error information with categorization and guidance
 */
export interface DetailedTriageError {
  pmid: string;
  code: string;
  message: string;
  category: 'network' | 'rate_limit' | 'validation' | 'processing' | 'timeout' | 'service_unavailable';
  retryable: boolean;
  suggestedAction?: string;
  technicalDetails?: string;
  retryAfterSeconds?: number;
  timestamp: string;
}

/**
 * Retry configuration for different error types
 */
export interface RetryConfig {
  maxAttempts: number;
  backoffStrategy: 'linear' | 'exponential';
  baseDelayMs: number;
  maxDelayMs: number;
  retryableErrors: string[];
}

/**
 * Context for error handling operations
 */
export interface ErrorHandlingContext {
  selectedPapers: string[];
  currentErrors: DetailedTriageError[];
  retryAttempts: Record<string, number>;
  lastRetryTimestamp?: string;
}

/**
 * Summary of batch error analysis
 */
export interface BatchErrorSummary {
  totalErrors: number;
  errorsByCategory: Record<DetailedTriageError['category'], number>;
  retryableCount: number;
  persistentCount: number;
  mostCommonError?: string;
  suggestedBatchAction?: 'retry_all' | 'retry_selected' | 'skip_failed' | 'contact_support';
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  backoffStrategy: 'exponential',
  baseDelayMs: 1000,
  maxDelayMs: 30000,
  retryableErrors: [
    'EHTA-AI-004', // Rate limit
    'EHTA-AI-500', // Internal server error
    'EHTA-PUBMED-010', // Upstream rate limit
    'EHTA-PUBMED-011', // Upstream error
    'EHTA-PUBMED-012', // Upstream timeout
    'EHTA-PUBMED-013', // Network/DNS error
    'NETWORK_ERROR',
    'TIMEOUT_ERROR'
  ]
};

/**
 * Error category mapping based on error codes
 */
const ERROR_CATEGORY_MAP: Record<string, DetailedTriageError['category']> = {
  // Network and connectivity errors
  'EHTA-PUBMED-013': 'network',
  'EHTA-PUBMED-012': 'timeout',
  'NETWORK_ERROR': 'network',
  'TIMEOUT_ERROR': 'timeout',
  
  // Rate limiting errors
  'EHTA-AI-004': 'rate_limit',
  'EHTA-PUBMED-010': 'rate_limit',
  
  // Validation errors
  'EHTA-AI-002': 'validation',
  'EHTA-AI-005': 'validation',
  'EHTA-PUBMED-001': 'validation',
  'EHTA-PUBMED-002': 'validation',
  'EHTA-PUBMED-003': 'validation',
  'EHTA-PUBMED-004': 'validation',
  'EHTA-PUBMED-005': 'validation',
  
  // Processing errors
  'EHTA-AI-003': 'processing',
  'EHTA-AI-500': 'processing',
  'EHTA-PUBMED-020': 'processing',
  'EHTA-PUBMED-021': 'processing',
  
  // Service unavailable
  'EHTA-AI-001': 'service_unavailable'
};

/**
 * Convert basic error to detailed error with categorization and guidance
 */
export function createDetailedTriageError(
  pmid: string,
  basicError: { code: string; message: string; retryAfterSeconds?: number },
  technicalDetails?: string
): DetailedTriageError {
  const category = ERROR_CATEGORY_MAP[basicError.code] || 'processing';
  const retryable = DEFAULT_RETRY_CONFIG.retryableErrors.includes(basicError.code);
  
  const detailedError: DetailedTriageError = {
    pmid,
    code: basicError.code,
    message: basicError.message,
    category,
    retryable,
    technicalDetails,
    retryAfterSeconds: basicError.retryAfterSeconds,
    timestamp: new Date().toISOString(),
    suggestedAction: '' // Will be set below
  };

  // Set suggested action using the error guidance function
  detailedError.suggestedAction = getErrorGuidance(detailedError);
  
  return detailedError;
}

/**
 * Get actionable error guidance based on error category and details
 */
export function getErrorGuidance(error: DetailedTriageError): string {
  switch (error.category) {
    case 'network':
      return 'Check your internet connection and try again. If the problem persists, the service may be temporarily unavailable.';
    
    case 'rate_limit':
      if (error.retryAfterSeconds) {
        const minutes = Math.ceil(error.retryAfterSeconds / 60);
        return `Rate limit reached. Please wait ${minutes} minute${minutes !== 1 ? 's' : ''} before retrying.`;
      }
      return 'Rate limit reached. Please wait a few minutes before retrying.';
    
    case 'validation':
      if (error.code === 'EHTA-AI-005') {
        return 'Too many papers selected. Please select 10 or fewer papers for analysis.';
      }
      if (error.code === 'EHTA-AI-002') {
        return 'This paper may have formatting issues or missing content. Try analyzing other papers.';
      }
      return 'The paper data appears to be invalid. Please try with different papers.';
    
    case 'processing':
      if (error.code === 'EHTA-AI-003') {
        return 'The AI service encountered an issue processing this paper. The content may be too complex or in an unsupported format.';
      }
      return 'Processing failed for this paper. This may be due to complex content or technical issues.';
    
    case 'timeout':
      return 'The analysis took too long to complete. Try analyzing fewer papers at once or retry later.';
    
    case 'service_unavailable':
      return 'The AI analysis service is currently unavailable. Please try again later or contact support if the issue persists.';
    
    default:
      return 'An unexpected error occurred. Please try again, and contact support if the problem continues.';
  }
}

/**
 * Determine if an error is temporary and likely to resolve with retry
 */
export function isTemporaryError(error: DetailedTriageError): boolean {
  return error.category === 'network' || 
         error.category === 'rate_limit' || 
         error.category === 'timeout' ||
         (error.category === 'processing' && error.code === 'EHTA-AI-500');
}

/**
 * Determine if an error is persistent and unlikely to resolve with retry
 */
export function isPersistentError(error: DetailedTriageError): boolean {
  return error.category === 'validation' || 
         error.category === 'service_unavailable' ||
         (error.category === 'processing' && error.code !== 'EHTA-AI-500');
}

/**
 * Calculate backoff delay for retry attempts
 */
export function calculateBackoffDelay(
  attempt: number, 
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): number {
  if (config.backoffStrategy === 'linear') {
    return Math.min(config.baseDelayMs * attempt, config.maxDelayMs);
  } else {
    // Exponential backoff with jitter
    const exponentialDelay = config.baseDelayMs * Math.pow(2, attempt - 1);
    const jitter = Math.random() * 0.1 * exponentialDelay; // 10% jitter
    return Math.min(exponentialDelay + jitter, config.maxDelayMs);
  }
}

/**
 * Check if an error should be retried based on attempt count and configuration
 */
export function shouldRetryError(
  error: DetailedTriageError, 
  currentAttempt: number, 
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): boolean {
  return error.retryable && 
         currentAttempt < config.maxAttempts && 
         config.retryableErrors.includes(error.code);
}

/**
 * Get user-friendly error summary for display
 */
export function getErrorSummary(error: DetailedTriageError): string {
  switch (error.category) {
    case 'network':
      return 'Connection issue';
    case 'rate_limit':
      return 'Rate limit reached';
    case 'validation':
      return 'Invalid paper data';
    case 'processing':
      return 'Processing failed';
    case 'timeout':
      return 'Request timed out';
    case 'service_unavailable':
      return 'Service unavailable';
    default:
      return 'Unknown error';
  }
}

/**
 * Get appropriate icon for error category
 */
export function getErrorIcon(error: DetailedTriageError): string {
  switch (error.category) {
    case 'network':
      return 'NET';
    case 'rate_limit':
      return 'RATE';
    case 'validation':
      return 'VALID';
    case 'processing':
      return 'PROC';
    case 'timeout':
      return 'TIME';
    case 'service_unavailable':
      return 'SERVICE';
    default:
      return 'ERROR';
  }
}